import { useState, useRef, useCallback } from "react";

/**
 * useImageCrop — manages crop state, image-load sizing, canvas crop,
 * and undo for the camera's image crop flow.
 */
export default function useImageCrop({
	showPreview,
	onCapture,
	onClose,
	setError,
	setCapturedImage,
	fetchBlobAsJpeg,
}) {
	const [showCrop, setShowCrop] = useState(false);
	const [imageToCrop, setImageToCrop] = useState(null);
	const [crop, setCrop] = useState(null);
	const imgRef = useRef(null);

	const onCropChange = (newCrop) => setCrop(newCrop);

	const onCropComplete = (newCrop) => {
		setCrop(newCrop);
	};

	const onImageLoaded = useCallback(
		(e) => {
			const img = e.currentTarget;
			if (img && !crop) {
				const calculate = () => {
					const dw = img.offsetWidth || img.clientWidth || img.width || 0;
					const dh = img.offsetHeight || img.clientHeight || img.height || 0;
					if (dw === 0 || dh === 0) {
						setTimeout(calculate, 50);
						return;
					}
					const size = Math.min(dw, dh);
					setCrop({
						unit: "px",
						x: Math.max(0, (dw - size) / 2),
						y: Math.max(0, (dh - size) / 2),
						width: size,
						height: size,
					});
				};
				requestAnimationFrame(() => setTimeout(calculate, 50));
			}
		},
		[crop]
	);

	const getCroppedImg = (image, cropData) => {
		if (!cropData || !image) {
			return Promise.reject(new Error("Invalid crop or image"));
		}
		return new Promise((resolve, reject) => {
			const canvas = document.createElement("canvas");
			const ctx = canvas.getContext("2d");
			const scaleX = image.naturalWidth / image.width;
			const scaleY = image.naturalHeight / image.height;
			const targetSize = 480;
			canvas.width = targetSize;
			canvas.height = targetSize;

			const px = {
				x: cropData.x * scaleX,
				y: cropData.y * scaleY,
				width: cropData.width * scaleX,
				height: cropData.height * scaleY,
			};
			ctx.drawImage(
				image,
				px.x,
				px.y,
				px.width,
				px.height,
				0,
				0,
				targetSize,
				targetSize
			);

			canvas.toBlob(
				(blob) => {
					if (!blob) {
						reject(new Error("Canvas is empty"));
						return;
					}
					resolve(URL.createObjectURL(blob));
				},
				"image/jpeg",
				0.95
			);
		});
	};

	const applyCrop = async () => {
		if (!imageToCrop || !crop || !imgRef.current) {
			return;
		}
		try {
			const croppedUrl = await getCroppedImg(imgRef.current, crop);
			setCapturedImage(croppedUrl);
			setShowCrop(false);
			setCrop(null);
			if (!showPreview) {
				const file = await fetchBlobAsJpeg(croppedUrl, "photo");
				onCapture(file);
				onClose();
			}
		} catch (err) {
			console.error("Error applying crop:", err);
			setError("Failed to apply crop. Please try again.");
		}
	};

	/** Clean up crop state only — caller handles camera restart. */
	const cancelCrop = () => {
		setShowCrop(false);
		setCrop(null);
		if (imageToCrop) {
			URL.revokeObjectURL(imageToCrop);
			setImageToCrop(null);
		}
	};

	const resetCrop = () => {
		if (imageToCrop) {
			URL.revokeObjectURL(imageToCrop);
			setImageToCrop(null);
		}
		setShowCrop(false);
		setCrop(null);
	};

	return {
		showCrop,
		setShowCrop,
		imageToCrop,
		setImageToCrop,
		crop,
		imgRef,
		onCropChange,
		onCropComplete,
		onImageLoaded,
		applyCrop,
		cancelCrop,
		resetCrop,
	};
}
